/**
 * WeKIT™ Career Clarity 360 Passport — presentation layer.
 *
 * Every component here is pure render. No scoring, no thresholds, no
 * re-wording: the sentences come from the dossier model, which reads the
 * sealed engine. If a number appears on this page it arrived with the
 * sentence that explains it.
 *
 * Built to survive print. Section wrappers carry `dossier-sheet`, which
 * becomes a page break in `@media print`, and each one prints a running
 * head and a WeKIT-branded foot carrying the page number and the seal.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Heart, Sparkles } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import OnetAttribution from "@/components/OnetAttribution";
import { ConvergenceVenn } from "./ConvergenceVenn";
import type {
  Dossier, FamilyFitment, ForTheirPeople, HowYouWork, IdentityPage, LoadBar,
  SpectrumGroup, SummaryRow, WhatPullsYou,
} from "../dossier";
import { REPORT_NAME } from "../dossier";

// -------------------------------------------------------------------------
// Running head / foot
// -------------------------------------------------------------------------

export interface DossierMeta {
  learnerName: string;
  generatedOn: string;
  reportHash: string | null;
}

const MetaContext = createContext<DossierMeta | null>(null);

/**
 * Page numbers are written to the DOM after layout rather than passed down,
 * because a section doesn't know its own position and the count has to
 * agree with what the printer actually paginates.
 */
function usePageNumbers(ready: boolean) {
  useEffect(() => {
    if (!ready) return;
    const sheets = Array.from(document.querySelectorAll<HTMLElement>(".dossier-sheet"));
    sheets.forEach((sheet, i) => {
      sheet.querySelectorAll("[data-dossier-page]").forEach((el) => {
        el.textContent = String(i + 1);
      });
      sheet.querySelectorAll("[data-dossier-total]").forEach((el) => {
        el.textContent = String(sheets.length);
      });
    });
  }, [ready]);
}

/** Repeated on every sheet so a printed page found on its own still says whose it is. */
export function SheetFrame() {
  const meta = useContext(MetaContext);
  return (
    <>
      <div className="dossier-running-head" aria-hidden="true">
        <span>WeKIT Career Clarity 360™ · {REPORT_NAME}</span>
        <span>{meta?.learnerName ?? ""}{meta?.generatedOn ? ` · ${meta.generatedOn}` : ""}</span>
      </div>
      <div className="dossier-running-foot" aria-hidden="true">
        <span className="dossier-foot-brand">WeKIT™ · Career Clarity 360</span>
        <span className="font-mono">
          {meta?.reportHash ? `Seal ${meta.reportHash.slice(0, 12)}` : "Unsealed preview"}
        </span>
        <span>
          Page <b data-dossier-page>1</b> of <b data-dossier-total>1</b>
        </span>
      </div>
    </>
  );
}

// -------------------------------------------------------------------------
// Chrome
// -------------------------------------------------------------------------

export function Sheet({
  number, kicker, title, intro, children,
}: {
  number?: string;
  kicker?: string;
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="dossier-sheet relative mx-auto w-full max-w-3xl px-6 py-12 md:py-16">
      <SheetFrame />
      <header className="mb-8">
        {kicker && (
          <div className="text-[11px] uppercase tracking-[0.28em] text-primary font-medium">
            {number ? `${number} · ` : ""}{kicker}
          </div>
        )}
        <h2 className="font-display text-3xl md:text-4xl font-medium mt-2 leading-tight">{title}</h2>
        {intro && (
          <p className="text-base text-muted-foreground mt-3 leading-relaxed max-w-2xl">{intro}</p>
        )}
      </header>
      {children}
    </section>
  );
}

/** A 0..100 bar that always sits beside its sentence. */
function Magnitude({ value, tone = "primary" }: { value: number; tone?: "primary" | "accent" }) {
  const width = Math.max(2, Math.min(100, value));
  return (
    <div
      className="h-2 w-full rounded-full bg-muted overflow-hidden"
      role="img"
      aria-label={`${value} out of 100`}
    >
      <div
        className={`h-full rounded-full ${tone === "primary" ? "bg-primary" : "bg-[hsl(var(--celebrate))]"}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function LowConfidenceNote() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-amber-500 align-middle">
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      Two answers disagreed — we kept the safer one
    </span>
  );
}

// -------------------------------------------------------------------------
// Cover
// -------------------------------------------------------------------------

export function DossierCover({ d }: { d: Dossier }) {
  return (
    <section className="dossier-sheet relative mx-auto w-full max-w-3xl px-6 pt-12 pb-16 md:pt-20">
      <SheetFrame />
      <div className="flex items-center gap-3">
        <WeKitLogo size="md" clickable={false} />
        <div>
          <div className="font-display text-lg font-medium leading-none">WeKIT</div>
          <div className="text-xs text-muted-foreground mt-1">Career Clarity 360™</div>
        </div>
      </div>

      <div className="mt-16 md:mt-24">
        <div className="text-[11px] uppercase tracking-[0.28em] text-primary font-medium">
          {d.bandLabel}
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-medium mt-4 leading-[1.05]">
          Your {REPORT_NAME}
        </h1>
        <p className="font-display text-2xl md:text-3xl text-primary mt-6 leading-snug">
          {d.headline}
        </p>
      </div>

      <dl className="mt-16 grid grid-cols-2 gap-x-8 gap-y-6 text-sm border-t border-border pt-8">
        <div>
          <dt className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Prepared for</dt>
          <dd className="mt-1 font-medium">{d.learnerName}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Date</dt>
          <dd className="mt-1 font-medium">{d.generatedOn}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Attempt</dt>
          <dd className="mt-1 font-mono text-xs">{d.attemptRef}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Verification</dt>
          <dd className="mt-1 font-mono text-xs break-all">
            {d.reportHash ? d.reportHash.slice(0, 16) : "On-device preview"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

// -------------------------------------------------------------------------
// Welcome letter + how to read
// -------------------------------------------------------------------------

export function DossierWelcome({ d }: { d: Dossier }) {
  const young = d.band === "12to14";
  return (
    <Sheet number="" kicker="Before you start" title="Read this part first.">
      <div className="space-y-5 text-base leading-relaxed text-foreground/90">
        <p>
          This isn't a verdict. Nothing in here closes a door, and nothing in here says what
          you're capable of. It's a careful description of how you like to work, what you need
          from a working day, what you can carry, and which kinds of work fit that — written on{" "}
          {d.generatedOn}, from the choices you made.
        </p>
        <p>
          We didn't ask you to rate yourself. Rating yourself is how most questionnaires go
          wrong: nobody ticks <em>disagree</em> on "I collaborate effectively". So instead we put
          you in situations where both options were reasonable and watched which one you reached
          for. {d.howYouWork.decisionsCounted > 0 && (
            <>That's what the archetype pages are built from.</>
          )}
        </p>
        <p>
          Where we're less sure, we say so on the page rather than smoothing it over. Where we
          have no data — a pay band we can't verify, for instance — we leave it blank instead of
          filling it in.{" "}
          {young
            ? "And because you're still early, you won't find job titles ranked in here. You'll find worlds of work, which is the honest unit at your age."
            : "Everything ranked in here is ranked for a reason we can show you."}
        </p>
        <p className="text-muted-foreground">
          Read it with someone at home. Argue with it. If a page doesn't sound like you, that's
          useful information, and you can run this again.
        </p>
      </div>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section I-A — How you work
// -------------------------------------------------------------------------

const BAND_LABEL = { high: "Leading", medium: "Supporting", low: "Quiet" } as const;
const BAND_STYLE = {
  high: "border-primary/50 bg-primary/10 text-primary",
  medium: "border-border bg-muted/40 text-foreground/80",
  low: "border-border bg-transparent text-muted-foreground",
} as const;

export function HowYouWorkSection({ h }: { h: HowYouWork }) {
  const chartData = h.rows.map((r) => ({ name: r.name, value: r.value }));
  return (
    <Sheet
      number="I"
      kicker="How you work"
      title={`Mostly a ${h.primary}. And also a ${h.secondary}.`}
      intro={`${h.primaryTagline}. Underneath that, ${h.secondaryTagline.toLowerCase()}. These come from the choices you made, not from anything you said about yourself.`}
    >
      <div className="rounded-3xl border border-border bg-card p-4 md:p-6">
        <div className="h-[300px] w-full" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="72%">
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis
                dataKey="name"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
              />
              <Radar
                dataKey="value"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.28}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <ul className="mt-4 space-y-3">
          {h.rows.map((r) => (
            <li key={r.name} className="grid grid-cols-[7.5rem_1fr_auto] items-center gap-3">
              <span className="text-sm font-medium">{r.name}</span>
              <Magnitude value={r.value} />
              <span className={`text-[10px] uppercase tracking-[0.16em] rounded-full border px-2 py-0.5 ${BAND_STYLE[r.band]}`}>
                {BAND_LABEL[r.band]}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {([
        ["Where your energy leads", h.leading],
        ["What supports it", h.supporting],
        ["Quieter for you — and that's fine", h.quiet],
      ] as const).map(([title, rows]) => rows.length > 0 && (
        <div key={title} className="mt-8">
          <h3 className="font-display text-xl font-medium">{title}</h3>
          <div className="mt-3 space-y-4">
            {rows.map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card/60 p-4">
                <div className="text-sm font-medium text-primary">{row.name}</div>
                <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{row.prose}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section I-B — What you need
// -------------------------------------------------------------------------

export function WhatYouNeedSection({ groups }: { groups: SpectrumGroup[] }) {
  return (
    <Sheet
      number="II"
      kicker="What you need from work"
      title="The conditions you work well in."
      intro="None of these are good or bad — they're requirements. A job that breaks two of them will wear you down even if you're brilliant at it. Marker further right means more of the thing on the right."
    >
      <div className="space-y-10">
        {groups.map((g) => (
          <div key={g.group}>
            <h3 className="font-display text-xl font-medium">{g.group}</h3>
            <div className="mt-4 space-y-6">
              {g.items.map((s) => (
                <div key={s.key}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium">{s.label}</span>
                    {s.lowConfidence && <LowConfidenceNote />}
                  </div>
                  <div className="mt-2 relative h-8" role="img"
                    aria-label={`${s.label}: ${s.value} out of 100 towards ${s.poleHigh}`}>
                    <div className="absolute inset-x-0 top-3 h-2 rounded-full bg-muted" />
                    <div
                      className="absolute top-1.5 h-5 w-5 -ml-2.5 rounded-full bg-primary border-2 border-background"
                      style={{ left: `${Math.max(3, Math.min(97, s.value))}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-muted-foreground">
                    <span>{s.poleLow}</span>
                    <span>{s.poleHigh}</span>
                  </div>
                  <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{s.meaning}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section I-C — What you can carry
// -------------------------------------------------------------------------

export function WhatYouCanCarrySection({ loads }: { loads: LoadBar[] }) {
  return (
    <Sheet
      number="III"
      kicker="What you can carry"
      title="How much a job can ask of you."
      intro="This one works differently. Being tougher than a job needs costs you nothing — but a job that demands more than you can carry is a real problem, so we only ever counted it against a match in that direction."
    >
      <div className="space-y-6">
        {loads.map((l) => (
          <div key={l.key} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium">{l.label}</span>
              <span className="text-xs text-muted-foreground">{l.value} / 100</span>
            </div>
            <div className="mt-2"><Magnitude value={l.value} tone="accent" /></div>
            <p className="text-sm text-foreground/85 mt-3 leading-relaxed">{l.meaning}</p>
            {l.value <= 40 && (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                <span className="text-amber-500">What we did about it: </span>{l.ifExceeded}
              </p>
            )}
            {l.lowConfidence && <div className="mt-2"><LowConfidenceNote /></div>}
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section I-D — What pulls you
// -------------------------------------------------------------------------

export function WhatPullsYouSection({ p }: { p: WhatPullsYou }) {
  if (p.empty) return null;
  return (
    <Sheet
      number="IV"
      kicker="What pulls you"
      title="The things you told us you care about."
      intro="You said these out loud rather than revealing them, so we weigh them differently — and we'd rather tell you exactly how much they moved your list than pretend they didn't count."
    >
      {p.passions.length > 0 && (
        <div>
          <h3 className="font-display text-xl font-medium">What you're drawn to</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {p.passions.map((item) => (
              <div key={item.label} className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Heart className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {item.label}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {p.dream && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-xl font-medium">When you were ten, you wanted to be…</h3>
          <p className="font-display text-2xl text-primary mt-2">{p.dream.label}</p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {p.dream.detail} We don't treat that as a plan. We treat it as a clue about what
            attracted you before anyone told you what was sensible — and it's worth about half of
            one of your other picks.
          </p>
        </div>
      )}

      {p.pulls.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-xl font-medium">The work that caught your eye</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {p.pulls.map((item) => (
              <li key={item.label}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm">
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.familyStrength.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-xl font-medium">Where all of that points</h3>
          <ul className="mt-3 space-y-2">
            {p.familyStrength.slice(0, 6).map((f) => (
              <li key={f.family} className="flex items-center gap-3 text-sm">
                <span className="w-48 shrink-0 truncate">{f.family}</span>
                <Magnitude value={Math.min(100, f.count * 34)} />
                <span className="text-xs text-muted-foreground shrink-0">
                  {f.count} {f.count === 1 ? "pick" : "picks"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-8 leading-relaxed border-t border-border pt-4">
        {p.weightNote}
      </p>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section II — Fitment by family
// -------------------------------------------------------------------------

const BREAKDOWN_LABELS: { key: keyof FamilyFitment["breakdown"]; label: string }[] = [
  { key: "howYouWork", label: "How you work" },
  { key: "whatMatters", label: "What matters to you" },
  { key: "theDay", label: "The shape of the day" },
  { key: "whatYouNeed", label: "What you need" },
  { key: "pay", label: "Pay" },
  { key: "outlook", label: "Outlook to 2040" },
];

export function FitmentSection({ rows, showsPay }: { rows: FamilyFitment[]; showsPay: boolean }) {
  const top = rows.slice(0, 8);
  const bottom = rows.slice(-3).reverse();
  return (
    <Sheet
      number="V"
      kicker="Fitment"
      title="Which parts of the world of work fit you."
      intro="Every career you're eligible for was scored, then grouped into its family. A family's score is the average of its strongest few roles — so a big family isn't punished for its own breadth."
    >
      <div className="space-y-4">
        {top.map((f, i) => (
          <div key={f.family} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-muted-foreground font-mono shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-lg font-medium truncate">{f.family}</span>
                {f.named && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em]
                    rounded-full border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 shrink-0">
                    <Sparkles className="h-3 w-3" aria-hidden="true" /> You named this
                  </span>
                )}
              </div>
              <span className="font-display text-xl text-primary shrink-0">{f.fitment}</span>
            </div>
            <div className="mt-2"><Magnitude value={f.fitment} /></div>

            <div className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {BREAKDOWN_LABELS.filter((b) => showsPay || b.key !== "pay").map((b) => (
                <div key={b.key} className="flex items-center gap-2 text-xs">
                  <span className="w-36 shrink-0 text-muted-foreground">{b.label}</span>
                  <Magnitude value={f.breakdown[b.key]} />
                  <span className="w-7 shrink-0 text-right tabular-nums text-muted-foreground">
                    {f.breakdown[b.key]}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              {f.eligible.toLocaleString("en-IN")} {f.eligible === 1 ? "role" : "roles"} open to you
              here, including{" "}
              {f.examples.map((e, idx) => (
                <span key={e.ciId}>
                  {idx > 0 && ", "}
                  <Link to={`/careers/${e.ciId}`} className="text-primary hover:underline">
                    {e.title}
                  </Link>
                </span>
              ))}.
            </p>
          </div>
        ))}
      </div>

      {bottom.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border bg-muted/30 p-5">
          <h3 className="font-display text-lg font-medium">Where the fit is weakest</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Included because ruling things out honestly is half the value of a report like this.
            Weak fit isn't inability — it's a mismatch between what these families demand daily
            and what you told us you want.
          </p>
          <ul className="mt-3 space-y-2">
            {bottom.map((f) => (
              <li key={f.family} className="flex items-center gap-3 text-sm">
                <span className="w-48 shrink-0 truncate">{f.family}</span>
                <Magnitude value={f.fitment} />
                <span className="w-7 shrink-0 text-right tabular-nums text-muted-foreground">
                  {f.fitment}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Section III — Summary
// -------------------------------------------------------------------------

export function ReachPlot({ rows }: { rows: SummaryRow[] }) {
  const shown = rows.slice(0, 10);
  const [active, setActive] = useState<string | null>(null);

  // Fit scores in a top-10 sit in a narrow band (e.g. 72–81), so absolute
  // positioning stacks every dot on top of the next. Scale to the visible
  // range instead, and spread exact ties so nothing hides behind a peer.
  const points = useMemo(() => {
    const fits = shown.map((r) => r.fit);
    const reaches = shown.map((r) => r.reach);
    const fitMin = Math.min(...fits);
    const fitMax = Math.max(...fits);
    const reachMin = Math.min(...reaches);
    const reachMax = Math.max(...reaches);
    const norm = (v: number, lo: number, hi: number) => (hi - lo < 1 ? 0.5 : (v - lo) / (hi - lo));

    const seen = new Map<string, number>();
    return shown.map((r, i) => {
      const fx = norm(r.reach, reachMin, reachMax);
      const fy = norm(r.fit, fitMin, fitMax);
      const key = `${Math.round(fx * 20)}:${Math.round(fy * 20)}`;
      const n = seen.get(key) ?? 0;
      seen.set(key, n + 1);
      // deterministic fan-out around the shared coordinate
      const angle = (n * 2 * Math.PI) / 5;
      const spread = n === 0 ? 0 : 4.5 + n * 0.9;
      return {
        row: r,
        index: i,
        left: Math.max(9, Math.min(91, 10 + fx * 80 + Math.cos(angle) * spread)),
        top: Math.max(12, Math.min(86, 88 - fy * 74 + Math.sin(angle) * spread)),
      };
    });
  }, [shown]);

  return (
    <div className="rounded-3xl border border-border bg-card p-4 sm:p-5">
      <div className="text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] text-muted-foreground">
        Fit against how far you'd have to travel
      </div>

      <figure className="mt-3 sm:mt-4">
        <div
          data-testid="reach-plot"
          className="relative h-56 sm:h-72 rounded-2xl border border-border bg-muted/20 overflow-hidden"
        >
          {/* grid */}
          <div aria-hidden="true" className="absolute inset-0">
            {[25, 50, 75].map((p) => (
              <div key={`h${p}`} className="absolute left-0 right-0 border-t border-border/40" style={{ top: `${p}%` }} />
            ))}
            {[25, 50, 75].map((p) => (
              <div key={`v${p}`} className="absolute top-0 bottom-0 border-l border-border/40" style={{ left: `${p}%` }} />
            ))}
          </div>

          {/* sweet-spot quadrant */}
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 h-1/2 w-1/2 bg-primary/5 border-r border-b border-dashed border-primary/30"
          />
          <span
            aria-hidden="true"
            className="absolute left-1.5 top-1.5 sm:left-2 sm:top-2 rounded bg-background/70 px-1 py-0.5 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] text-primary"
          >
            Sweet spot
          </span>
          <span
            aria-hidden="true"
            className="absolute left-1.5 bottom-1.5 sm:left-2 sm:bottom-2 rounded bg-background/70 px-1 py-0.5 text-[9px] sm:text-[10px] text-muted-foreground"
          >
            Weaker fit
          </span>
          <span
            aria-hidden="true"
            className="absolute right-1.5 bottom-1.5 sm:right-2 sm:bottom-2 rounded bg-background/70 px-1 py-0.5 text-[9px] sm:text-[10px] text-muted-foreground"
          >
            Longer route in →
          </span>

          {points.map((p) => {
            const label = `${p.index + 1}. ${p.row.title} — rank ${p.index + 1}, fit ${p.row.fit}, ${p.row.distance}`;
            const isActive = active === p.row.ciId;
            const onLeft = p.left < 50;
            return (
              <div
                key={p.row.ciId}
                className="absolute"
                style={{ left: `${p.left}%`, top: `${p.top}%` }}
              >
                <button
                  type="button"
                  data-testid={`reach-point-${p.row.ciId}`}
                  className="absolute flex h-7 w-7 sm:h-6 sm:w-6 -ml-3.5 -mt-3.5 sm:-ml-3 sm:-mt-3 items-center justify-center rounded-full bg-primary text-[10px] font-medium tabular-nums text-primary-foreground ring-2 ring-background transition-transform hover:scale-125 focus-visible:scale-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={label}
                  title={label}
                  onMouseEnter={() => setActive(p.row.ciId)}
                  onMouseLeave={() => setActive((c) => (c === p.row.ciId ? null : c))}
                  onFocus={() => setActive(p.row.ciId)}
                  onBlur={() => setActive((c) => (c === p.row.ciId ? null : c))}
                >
                  {p.index + 1}
                </button>
                {isActive && (
                  <div
                    role="tooltip"
                    data-testid={`reach-tooltip-${p.row.ciId}`}
                    className={`absolute bottom-4 z-10 w-40 sm:w-48 rounded-xl border border-border bg-popover p-2.5 text-left shadow-lg ${
                      onLeft ? "left-2" : "right-2"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                      Rank {p.index + 1}
                    </div>
                    <div className="text-xs font-medium leading-snug mt-0.5">{p.row.title}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Fit {p.row.fit} · {p.row.distance}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <figcaption className="text-xs text-muted-foreground mt-3 leading-relaxed">
          Each number is a match below — tap or hover one for its detail. Higher up means it fits you
          better; further right means a longer route in. Positions are relative to your own top ten,
          not an absolute scale.
        </figcaption>
      </figure>

      <ol className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
        {shown.map((r, i) => (
          <li key={r.ciId} className="flex gap-2">
            <span className="w-4 shrink-0 text-right tabular-nums text-primary">{i + 1}</span>
            <span className="truncate">
              {r.title} — fit {r.fit}, {r.distance.toLowerCase()}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}



export function SummarySection({ rows, showsPay }: { rows: SummaryRow[]; showsPay: boolean }) {
  if (rows.length === 0) return null;
  return (
    <Sheet
      number="VI"
      kicker="Your matches"
      title="The work that fits, and why."
      intro="Ranked, with the reason attached. No match here is a recommendation to commit — each one is a lead worth an afternoon of research."
    >
      <ReachPlot rows={rows} />

      <div className="mt-8 space-y-4">
        {rows.slice(0, 10).map((r, i) => (
          <article key={r.ciId} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground font-mono">
                  {String(i + 1).padStart(2, "0")} · {r.family}
                </div>
                <h3 className="font-display text-xl font-medium mt-0.5">{r.title}</h3>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl text-primary">{r.fit}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  {r.tier}
                </div>
              </div>
            </div>

            <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">Route in:</dt>
                <dd>{r.distance}</dd>
              </div>
              {showsPay && (
                <div className="flex gap-1.5">
                  <dt className="text-muted-foreground">Typical pay:</dt>
                  <dd>{r.salary}</dd>
                </div>
              )}
            </dl>

            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary">Why it matched</div>
              <ul className="mt-1.5 space-y-1">
                {r.fits.map((f) => (
                  <li key={f} className="text-sm text-foreground/85 leading-relaxed">{f}.</li>
                ))}
              </ul>
            </div>

            {r.watch && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-[0.18em] text-amber-500">Worth knowing</div>
                <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{r.watch}.</p>
              </div>
            )}

            {r.flags.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2">
                {r.flags.map((f) => (
                  <li key={f}
                    className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                    {f}
                  </li>
                ))}
              </ul>
            )}

            <Link
              to={`/careers/${r.ciId}`}
              className="mt-4 inline-flex items-center gap-2 min-h-[44px] text-sm text-primary hover:underline
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg no-print"
            >
              Read the full page on {r.title} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </article>
        ))}
      </div>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Next steps + provenance
// -------------------------------------------------------------------------

export function NextStepsSection({ d }: { d: Dossier }) {
  return (
    <Sheet
      number="VII"
      kicker="What to do next"
      title="Four things worth doing this month."
      intro="A report is only worth the action it causes. These are ordered, cheap, and reversible."
    >
      <ol className="space-y-4">
        {d.nextSteps.map((s, i) => (
          <li key={s.title} className="rounded-2xl border border-border bg-card p-5 flex gap-4">
            <span className="font-display text-2xl text-primary/50 shrink-0 tabular-nums">
              {i + 1}
            </span>
            <div>
              <h3 className="font-display text-lg font-medium">{s.title}</h3>
              <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}

export function ProvenanceSection({ d }: { d: Dossier }) {
  const p = d.provenance;
  return (
    <Sheet number="VIII" kicker="Our working" title="How this report was made.">
      <dl className="grid gap-4 sm:grid-cols-3">
        {[
          ["Careers read", p.totalConsidered.toLocaleString("en-IN")],
          ["Open to you now", p.eligiblePool.toLocaleString("en-IN")],
          ["Set aside on study", p.ruledOutByStudy.toLocaleString("en-IN")],
        ].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-border bg-card p-4">
            <dt className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{k}</dt>
            <dd className="font-display text-2xl mt-1">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 space-y-4 text-sm text-foreground/85 leading-relaxed">
        {p.studyRelaxed && (
          <p className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
            Your answers on further study left very little open, so rather than show you an empty
            list we stretched that one limit by a single step. Everything above assumes you'd
            consider slightly more study than you told us — worth knowing before you plan around it.
          </p>
        )}

        {p.lowConfidenceSlots.length > 0 && (
          <p className="rounded-2xl border border-border bg-card p-4">
            On {p.lowConfidenceSlots.length}{" "}
            {p.lowConfidenceSlots.length === 1 ? "measure" : "measures"}, two different questions
            gave us two different answers. We kept the more cautious value and flagged it on the
            page rather than averaging the disagreement away.
          </p>
        )}

        <div>
          <h3 className="font-display text-lg font-medium">What this report does not measure</h3>
          <ul className="mt-2 space-y-2 list-disc pl-5">
            {p.notMeasured.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>

        <p className="text-xs text-muted-foreground border-t border-border pt-4">
          {p.engine} · pay and demand figures resolved from verified market data where available,
          left blank where not · generated {d.generatedOn} · reference {d.attemptRef}
          {d.reportHash && <> · sealed report {d.reportHash.slice(0, 16)}</>}
        </p>

        {/* Licence condition — printed and exported copies carry it too. */}
        <OnetAttribution className="pt-2" />
      </div>
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Identity page
// -------------------------------------------------------------------------

/**
 * The one page a person would screenshot. Meaning first, magnitude second —
 * every bar sits under the sentence that explains it, and there is no pay,
 * no ranked title and no raw score on this sheet.
 */
export function IdentitySheet({ identity, bandLabel }: { identity: IdentityPage; bandLabel: string }) {
  return (
    <section className="dossier-sheet relative mx-auto w-full max-w-3xl px-6 py-12 md:py-16">
      <SheetFrame />
      <div className="rounded-3xl border border-primary/30 bg-primary/5 p-6 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-primary font-medium">
          {bandLabel}
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-medium mt-3 leading-[1.08]">
          {identity.pairLabel}
        </h2>
        <p className="text-base md:text-lg text-foreground/90 mt-4 leading-relaxed max-w-2xl">
          {identity.sentence}
        </p>
        {identity.causeLine && (
          <p className="text-sm text-primary mt-3 inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {identity.causeLine}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-6">
          {identity.name} · {identity.shareLine}
        </p>
      </div>

      <div className="mt-8 space-y-6">
        {identity.signals.map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
            <div className="text-sm font-medium">{s.label}</div>
            <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{s.meaning}</p>
            <div className="mt-3"><Magnitude value={s.value} /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// For the people around you
// -------------------------------------------------------------------------

export function ForTheirPeopleSheet({ f }: { f: ForTheirPeople }) {
  return (
    <Sheet
      kicker={f.addressee}
      title="For the people around you."
      intro={f.intro}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <h3 className="font-display text-lg font-medium">What this says</h3>
          <ul className="mt-3 space-y-2 text-sm text-foreground/85 leading-relaxed list-disc pl-5">
            {f.whatItSays.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-medium">What it doesn't say</h3>
          <ul className="mt-3 space-y-2 text-sm text-foreground/85 leading-relaxed list-disc pl-5">
            {f.whatItDoesNotSay.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="font-display text-xl font-medium">Three questions worth asking this week</h3>
        <ol className="mt-3 space-y-3">
          {f.questions.map((q, i) => (
            <li key={q} className="rounded-2xl border border-border bg-card/60 p-4 flex gap-3">
              <span className="text-primary font-display text-lg leading-none">{i + 1}</span>
              <p className="text-sm text-foreground/85 leading-relaxed">{q}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
        <h3 className="font-display text-lg font-medium">One thing not to say</h3>
        <p className="font-display text-xl text-foreground mt-2">{f.dontSay.line}</p>
        <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{f.dontSay.because}</p>
      </div>

      {f.fromObserver && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-medium">{f.fromObserver.title}</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {f.fromObserver.kept.map((k) => (
              <li
                key={k}
                className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm text-foreground/85"
              >
                {k}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">{f.fromObserver.note}</p>
        </div>
      )}

      <p className="mt-6 rounded-2xl border border-border bg-card p-5 text-sm leading-relaxed">
        {f.oneThing}
      </p>
    </Sheet>
  );
}


// -------------------------------------------------------------------------
// Whole-report composer
// -------------------------------------------------------------------------

/**
 * Every sheet, in order, wrapped in the running-head context. Shared by the
 * owner's route and the read-only shared link so a recipient sees exactly
 * the pages the learner saw — no more, and never anything editable.
 */
/**
 * Convergence of Passions & Careers. Print-first: the diagram renders, but
 * every claim in it is also written out, because a PDF cannot be hovered and
 * a screen reader cannot see a ring.
 */
function ConvergenceSection({ d }: { d: Dossier }) {
  const c = d.convergence;
  return (
    <Sheet
      kicker="Convergence"
      title="Where your interests meet"
      intro={c.purpose ?? c.method}
    >
      <ConvergenceVenn data={c} printable />
    </Sheet>
  );
}

export function DossierBody({ d }: { d: Dossier }) {
  usePageNumbers(true);
  return (
    <MetaContext.Provider
      value={{ learnerName: d.learnerName, generatedOn: d.generatedOn, reportHash: d.reportHash }}
    >
      <DossierCover d={d} />
      <IdentitySheet identity={d.identity} bandLabel={d.bandLabel} />
      <DossierWelcome d={d} />
      <HowYouWorkSection h={d.howYouWork} />
      <WhatYouNeedSection groups={d.whatYouNeed} />
      <WhatYouCanCarrySection loads={d.whatYouCanCarry} />
      <WhatPullsYouSection p={d.whatPullsYou} />
      <FitmentSection rows={d.fitment} showsPay={d.showsPay} />
      <ConvergenceSection d={d} />
      {d.showsRankedTitles && <SummarySection rows={d.summary} showsPay={d.showsPay} />}
      <ForTheirPeopleSheet f={d.forTheirPeople} />
      <NextStepsSection d={d} />
      <ProvenanceSection d={d} />
    </MetaContext.Provider>
  );
}
